const YOUTUBE_RE =
  /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9_-]{11}/i;

/**
 * The `class` attribute of an inline emoji IMAGE. Two classes, because a body
 * can carry either: `emoji` is the migrated Discourse form, `theta-emoji` is
 * what `renderMarkdown`'s custom-emoji rule writes for a group's own
 * `:shortcode:` (OSO-431).
 *
 * The class is matched as ONE TOKEN among however many the attribute holds,
 * not as the whole attribute. Written as a source string and shared, because
 * the second copy — `/\bclass="theta-emoji"/` in `cookedToPlainText` — was
 * both narrower shapes at once: only the new class, and only when it stood
 * alone. A quoted emoji-only message that had been MIGRATED therefore still
 * fell through to the image branch and read "📷 Billede" (OSO-569), which is
 * the exact symptom the test below it says it exists to remove.
 */
const EMOJI_CLASS_SOURCE = String.raw`\bclass="[^"]*\b(?:emoji|theta-emoji)\b[^"]*"`;

/** An inline emoji IMAGE, whole tag. */
const EMOJI_IMG = new RegExp(String.raw`<img\b[^>]*${EMOJI_CLASS_SOURCE}[^>]*>`, "gi");

/** Whether a single `<img …>` tag, already isolated, is one of those. */
const EMOJI_IMG_CLASS = new RegExp(EMOJI_CLASS_SOURCE, "i");

/**
 * One whole emoji — a standard emoji is plain text in the body, not an image,
 * and "one emoji" is very often several code points.
 *
 * Getting the presentation test wrong is wrong in both directions.
 * `\p{Extended_Pictographic}` alone is too wide: `©`, `™` and `▪` are
 * pictographic and render as text unless a variation selector says otherwise,
 * so a body reading `©` counted as "nothing but emoji" and drew at double size.
 * `\p{Emoji}` and `\p{Emoji_Component}` are wider still — both match ASCII
 * digits, making "2026" an emoji. Matching single code points is too narrow: a
 * keycap (`1\uFE0F\u20E3`) is a digit, a variation selector and a combining
 * mark, none pictographic, so it was never recognised.
 *
 * So: a code point that is emoji by default (`\p{Emoji_Presentation}`), or a
 * text-presentation pictograph asked to be one with VS16 — plus the three
 * shapes built from pieces that are not themselves emoji: the keycap, the
 * two-regional-indicator flag, and skin tones and ZWJ-joined parts trailing
 * whichever base matched.
 */
const EMOJI_SEQUENCE = new RegExp(
  "(?:" +
    // A keycap: `1\uFE0F\u20E3`. Alternated FIRST so the digit is consumed as
    // part of it rather than left behind by a later arm.
    "[0-9#*]\\uFE0F?\\u20E3" +
    "|" +
    // A flag: two regional indicators, which are letters on their own.
    "[\\u{1F1E6}-\\u{1F1FF}]{2}" +
    "|" +
    // A base that is emoji by default, or one made emoji by VS16…
    "(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F)" +
    // …the modifiers that attach to it…
    "(?:\\uFE0F|[\\u{1F3FB}-\\u{1F3FF}])*" +
    // …and any ZWJ-joined continuation (a family, a profession).
    "(?:\\u200D(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F?)" +
    "(?:\\uFE0F|[\\u{1F3FB}-\\u{1F3FF}])*)*" +
  ")",
  "gu",
);

/**
 * Is this body nothing but emoji?
 *
 * Such a body is drawn at double size (the convention every chat app shares),
 * which is why "nothing but" has to be strict: one word alongside the emoji
 * and it is ordinary text at ordinary size. Whitespace and the block wrappers
 * the renderer puts around it (`<p>`, `<br>`) do not count as content; a link
 * or a picture does, whatever surrounds it.
 */
export function isEmojiOnlyBody(html: string): boolean {
  let emoji = 0;
  const rest = html.replace(EMOJI_IMG, () => {
    emoji += 1;
    return "";
  });
  if (/<(?:a|img)\b/i.test(rest)) return false;
  const text = rest
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(EMOJI_SEQUENCE, () => {
      emoji += 1;
      return "";
    })
    .trim();
  return emoji > 0 && text.length === 0;
}

/** Regex that matches a YouTube URL and captures the 11-char video ID (group 1). */
const YOUTUBE_ID_RE =
  /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/gi;

/** Extract all unique YouTube video IDs from plain text (raw markdown, not HTML). */
export function extractYoutubeIdsFromText(text: string): string[] {
  const ids: string[] = [];
  const re = new RegExp(YOUTUBE_ID_RE.source, YOUTUBE_ID_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) ids.push(m[1]);
  return [...new Set(ids)];
}

/**
 * A link Discourse oneboxed, plus whatever metadata it managed to resolve.
 *
 * Everything but `url` is optional: an unresolved onebox is just an anchor,
 * while a resolved one is an `<aside>` carrying the target page's title,
 * description, thumbnail and favicon.
 */
export interface OneboxRef {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
}

/**
 * A whole resolved onebox card, or the opening tag of an unresolved one.
 * The `<aside>` alternative comes first so the anchors *inside* a card are
 * consumed with it rather than reported as oneboxes of their own.
 */
const ONEBOX_RE =
  /<aside\s[^>]*class="[^"]*onebox[^"]*"[^>]*>[\s\S]*?<\/aside>|<a\s[^>]*class="[^"]*onebox[^"]*"[^>]*>/gi;

/** Just the `<aside>` half, for stripping cards out of the body. */
const ONEBOX_ASIDE_RE =
  /<aside\s[^>]*class="[^"]*onebox[^"]*"[^>]*>[\s\S]*?<\/aside>/gi;

/**
 * HTML 4.0's Latin-1 named entities, in codepoint order from U+00A0 to U+00FF.
 * Packed as a list rather than spelled out: a European page title is exactly
 * where a stray `&oslash;` would otherwise reach a Danish reader verbatim.
 */
const LATIN1_ENTITIES =
  "nbsp iexcl cent pound curren yen brvbar sect uml copy ordf laquo not shy reg macr deg plusmn sup2 sup3 acute micro para middot cedil sup1 ordm raquo frac14 frac12 frac34 iquest Agrave Aacute Acirc Atilde Auml Aring AElig Ccedil Egrave Eacute Ecirc Euml Igrave Iacute Icirc Iuml ETH Ntilde Ograve Oacute Ocirc Otilde Ouml times Oslash Ugrave Uacute Ucirc Uuml Yacute THORN szlig agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml igrave iacute icirc iuml eth ntilde ograve oacute ocirc otilde ouml divide oslash ugrave uacute ucirc uuml yacute thorn yuml".split(
    " "
  );

const NAMED_ENTITIES: Record<string, string> = {
  ...Object.fromEntries(
    LATIN1_ENTITIES.map((name, i) => [name, String.fromCharCode(0xa0 + i)])
  ),
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  euro: "€",
  hellip: "…",
  mdash: "—",
  ndash: "–",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
};

/**
 * Decode the HTML entities Discourse escapes into cooked markup, so a title
 * reads `Ost & brød` rather than `Ost &amp; br&#248;d` once React puts it in a
 * text node. Deliberately a *single* pass: `&amp;lt;` must come out as the
 * literal `&lt;`, not as `<`. Unknown entities are left alone — showing
 * `&frasl;` beats swallowing it.
 */
function decodeEntities(text: string): string {
  return text.replace(
    /&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi,
    (match, ref: string) => {
      if (ref.startsWith("#")) {
        const code =
          ref[1] === "x" || ref[1] === "X"
            ? parseInt(ref.slice(2), 16)
            : parseInt(ref.slice(1), 10);
        if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
        try {
          return String.fromCodePoint(code);
        } catch {
          return match;
        }
      }
      // Exact first: `&Aring;` and `&aring;` are different characters.
      return NAMED_ENTITIES[ref] ?? NAMED_ENTITIES[ref.toLowerCase()] ?? match;
    }
  );
}

/** Tag-free, entity-decoded, whitespace-collapsed text of an HTML fragment. */
function textOf(html: string | undefined): string | undefined {
  if (!html) return undefined;
  const text = decodeEntities(html.replace(/<[^>]*>/g, ""))
    .replace(/\s+/g, " ")
    .trim();
  return text || undefined;
}

/** `src` of the first `<img>` in `block` carrying `className`, any attribute order. */
function imgSrc(block: string, className: string): string | undefined {
  const hasClass = new RegExp(`class="[^"]*\\b${className}\\b[^"]*"`, "i");
  for (const tag of block.match(/<img\b[^>]*>/gi) ?? []) {
    if (!hasClass.test(tag)) continue;
    const src = tag.match(/src="([^"]+)"/i)?.[1];
    if (src) return decodeEntities(src);
  }
  return undefined;
}

/** Read a resolved onebox card. Returns null for YouTube (embedded instead). */
function parseAside(block: string): OneboxRef | null {
  // Discourse puts the canonical URL on the aside; older/odd oneboxes only
  // have it on the first link inside.
  const raw =
    block.match(/data-onebox-src="([^"]+)"/i)?.[1] ??
    block.match(/<a\b[^>]*href="([^"]+)"/i)?.[1];
  if (!raw) return null;
  const url = decodeEntities(raw);
  if (YOUTUBE_RE.test(url)) return null;

  // The description is the <p> of the card body — scope the search there so a
  // stray paragraph in the header cannot stand in for it.
  const bodyStart = block.search(
    /<[a-z]+\b[^>]*class="[^"]*\bonebox-body\b[^"]*"[^>]*>/i
  );
  const body = bodyStart === -1 ? block : block.slice(bodyStart);

  return {
    url,
    title: textOf(block.match(/<h3\b[^>]*>([\s\S]*?)<\/h3>/i)?.[1]),
    description: textOf(body.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i)?.[1]),
    image: imgSrc(block, "thumbnail"),
    favicon: imgSrc(block, "site-icon"),
  };
}

/** Read an unresolved onebox (`<a class="onebox">`, incl. `inline-onebox`). */
function parseAnchor(tag: string): OneboxRef | null {
  const raw = tag.match(/href="([^"]+)"/i)?.[1];
  if (!raw) return null;
  const url = decodeEntities(raw);
  return YOUTUBE_RE.test(url) ? null : { url };
}

/**
 * Extract every Discourse onebox from cooked HTML, in document order, with the
 * metadata Discourse already resolved for it.
 *
 * Both shapes: `<a class="onebox">` (URL only, covering `inline-onebox`) and
 * `<aside class="onebox">` (the resolved card). YouTube URLs are excluded —
 * they become embeds.
 *
 * Reading the card's metadata is the point: `LinkPreview` would otherwise
 * re-fetch the page through the API and *downgrade* to a bare domain link
 * whenever the target site blocks us.
 */
export function extractOneboxes(html: string): OneboxRef[] {
  const byUrl = new Map<string, OneboxRef>();
  const re = new RegExp(ONEBOX_RE.source, ONEBOX_RE.flags);
  let m: RegExpExecArray | null;

  while ((m = re.exec(html)) !== null) {
    const block = m[0];
    const ref = /^<aside/i.test(block) ? parseAside(block) : parseAnchor(block);
    if (!ref) continue;

    // One card per URL. The same link pasted twice can arrive once as a
    // resolved aside and once as a bare inline onebox; merging keeps whichever
    // occurrence carried the metadata (and keeps React keys unique).
    const seen = byUrl.get(ref.url);
    if (!seen) {
      byUrl.set(ref.url, ref);
      continue;
    }
    seen.title ??= ref.title;
    seen.description ??= ref.description;
    seen.image ??= ref.image;
    seen.favicon ??= ref.favicon;
  }

  return [...byUrl.values()];
}

/**
 * URLs of every onebox in the HTML, excluding YouTube (handled as embeds).
 * Metadata-free view of {@link extractOneboxes}.
 */
export function extractOneboxUrls(html: string): string[] {
  return extractOneboxes(html).map((o) => o.url);
}

/**
 * Post-process Discourse cooked HTML for link previews and embeds. The rules
 * below are ordered, and the ordering is load-bearing where noted.
 */
export function processCooked(html: string): string {
  let result = html;

  // 1. Discourse lazyYT: <div class="lazyYT" data-youtube-id="ID" ...>
  result = result.replace(
    /<div[^>]*class="[^"]*lazyYT[^"]*"[^>]*data-youtube-id="([^"]+)"[^>]*>[\s\S]*?<\/div>/gi,
    (_match, videoId: string) => youtubeEmbed(videoId)
  );

  // 2. Discourse lazy-video: <div class="lazy-video-container" data-video-id="ID" data-provider-name="youtube" ...>
  result = result.replace(
    /<div[^>]*class="[^"]*lazy-video-container[^"]*"[^>]*data-video-id="([^"]+)"[^>]*data-provider-name="youtube"[^>]*>[\s\S]*?<\/div>/gi,
    (_match, videoId: string) => youtubeEmbed(videoId)
  );
  // Also handle the reverse attribute order
  result = result.replace(
    /<div[^>]*class="[^"]*lazy-video-container[^"]*"[^>]*data-provider-name="youtube"[^>]*data-video-id="([^"]+)"[^>]*>[\s\S]*?<\/div>/gi,
    (_match, videoId: string) => youtubeEmbed(videoId)
  );

  // 3. YouTube onebox as <aside> with YouTube thumbnail image
  //    <aside class="onebox" ...>...<img src="https://img.youtube.com/vi/ID/..."...>...</aside>
  result = result.replace(
    /<aside[^>]*class="[^"]*onebox[^"]*"[^>]*>[\s\S]*?<img[^>]*src="https:\/\/img\.youtube\.com\/vi\/([^/"]+)\/[^"]*"[^>]*>[\s\S]*?<\/aside>/gi,
    (_match, videoId: string) => youtubeEmbed(videoId)
  );

  // 4. YouTube onebox as <p>...<iframe class="youtube-onebox" src="...youtube.com/embed/ID?...">...</p>
  //    Discourse renders these with a hidden thumbnail img + an iframe; replace the whole <p>
  result = result.replace(
    /<p>[\s\S]*?<iframe[^>]*class="[^"]*youtube-onebox[^"]*"[^>]*src="https:\/\/www\.youtube\.com\/embed\/([a-zA-Z0-9_-]{11})[^"]*"[^>]*>[\s\S]*?<\/iframe>[\s\S]*?<\/p>/gi,
    (_match, videoId: string) => youtubeEmbed(videoId)
  );

  // 5. Any YouTube link anywhere in the post → embed.
  //    Rules 1–4 already replaced Discourse's own embed formats, so any remaining
  //    <a href="youtube..."> is a bare link the user typed or pasted.
  result = result.replace(
    /<a[^>]*href="https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^"]*"[^>]*>[^<]*<\/a>/gi,
    (_match, videoId: string) => youtubeEmbed(videoId)
  );

  // 6. Drop the resolved onebox card. Discourse renders a link it managed to
  //    resolve as an <aside> with markup and CSS of its own, so the same link
  //    looked one way resolved and another way not. RichContent renders a
  //    LinkPreview from the card's own metadata instead (extractOneboxes reads
  //    it off the raw HTML first). It also takes the layout bug with it: the
  //    card's .onebox-body flex row has a ~390px min-content width, which pushed
  //    a shrink-to-fit DM bubble off a phone screen.
  //    MUST run after rules 1–5 — they recognise a YouTube onebox by what is
  //    *inside* the aside, and rule 5 can leave the embed in there.
  result = result.replace(
    ONEBOX_ASIDE_RE,
    (match) => match.match(/<div class="youtube-embed">[\s\S]*?<\/div>/i)?.[0] ?? ""
  );

  // 7. Show what was actually linked. Discourse hides the URL two ways: inline
  //    oneboxes get their link text rewritten to the target page title (every
  //    Theta link then reads "Theta"), and standalone ones we used to strip
  //    outright. Put the href back as the link text — the LinkPreview card
  //    rendered below the post carries the title and description.
  //    Matches onebox, inline-onebox and inline-onebox-loading, in any
  //    attribute order. YouTube oneboxes are already gone (rule 5).
  result = result.replace(
    /<a\s[^>]*class="[^"]*\bonebox[^"]*"[^>]*>[\s\S]*?<\/a>/gi,
    (match) => {
      const href = match.match(/href="([^"]+)"/i)?.[1];
      if (!href) return match;
      // Replace only the text node, keeping the opening tag (and its
      // target/rel) intact. A function replacer keeps `$` in the URL literal.
      return match.replace(/>[\s\S]*<\/a>$/, () => `>${href}</a>`);
    }
  );

  return result;
}

/**
 * Image URLs from cooked HTML (emoji and site icons excluded) plus the HTML
 * with those images removed. Prefers the lightbox href (full size) over the
 * thumbnail `src`.
 */
export function extractAndStripImages(html: string): {
  html: string;
  images: string[];
} {
  const images: string[] = [];

  // 0. Hide onebox cards from the stripper. A resolved link renders as
  //    <aside class="onebox"> carrying the linked page's og:image and favicon;
  //    those belong to the card, not to the post's gallery. Stripping them
  //    would both gut the card and put the linked site's logo in the gallery —
  //    the client-side twin of the bug Theta::PostImages fixes server-side.
  //    An HTML comment stands in for the card: none of the rules below match
  //    one, so it comes back untouched at the end.
  const oneboxes: string[] = [];
  const hidden = html.replace(
    /<aside\b[^>]*class="[^"]*\bonebox\b[^"]*"[^>]*>[\s\S]*?<\/aside>/gi,
    (match) => `<!--theta-onebox-${oneboxes.push(match) - 1}-->`
  );

  // 1. Discourse lightbox wrappers: <div class="lightbox-wrapper"><a class="lightbox" href="FULL">...<img src="THUMB" ...></a>...</div>
  let result = hidden.replace(
    /<div[^>]*class="[^"]*lightbox-wrapper[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    (match) => {
      const hrefMatch = match.match(/<a[^>]*class="[^"]*lightbox[^"]*"[^>]*href="([^"]+)"/i)
        || match.match(/<a[^>]*href="([^"]+)"[^>]*class="[^"]*lightbox[^"]*"/i);
      const imgMatch = match.match(/<img[^>]*src="([^"]+)"/i);
      const url = hrefMatch?.[1] ?? imgMatch?.[1];
      if (url) images.push(url);
      return "";
    }
  );

  // 2. Bare <a class="lightbox" href="FULL"><img src="THUMB" ...></a>
  result = result.replace(
    /<a[^>]*class="[^"]*lightbox[^"]*"[^>]*>[\s\S]*?<\/a>/gi,
    (match) => {
      const hrefMatch = match.match(/href="([^"]+)"/i);
      const imgMatch = match.match(/<img[^>]*src="([^"]+)"/i);
      const url = hrefMatch?.[1] ?? imgMatch?.[1];
      if (url) images.push(url);
      return "";
    }
  );

  // 3. Bare <img> tags that are NOT emoji or site icons
  result = result.replace(/<img\b[^>]*>/gi, (match) => {
    if (/class="[^"]*\b(emoji|site-icon)\b[^"]*"/i.test(match)) return match;
    const srcMatch = match.match(/src="([^"]+)"/i);
    if (srcMatch) images.push(srcMatch[1]);
    return "";
  });

  // 4. Clean up paragraphs left empty by stripped images
  result = result.replace(/<p>\s*<\/p>/gi, "");

  // 5. Put the onebox cards back exactly as they were.
  if (oneboxes.length > 0) {
    result = result.replace(
      /<!--theta-onebox-(\d+)-->/g,
      (_m, i: string) => oneboxes[Number(i)] ?? ""
    );
  }

  return { html: result, images };
}

/**
 * Pull the youtube-embed divs out of the HTML, so the caller can render them at
 * card level — outside the avatar-offset flex row, where their horizontal
 * margins are symmetric.
 */
export function extractYoutubeEmbeds(html: string): {
  html: string;
  videoIds: string[];
} {
  const videoIds: string[] = [];
  const result = html.replace(
    /<div class="youtube-embed"><iframe[^>]*src="https:\/\/www\.youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]{11})[^"]*"[^>]*><\/iframe><\/div>/gi,
    (_match, videoId: string) => {
      videoIds.push(videoId);
      return "";
    }
  );
  return { html: result, videoIds };
}

const AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|m4a|webm|flac)(\?[^"]*)?$/i;

/**
 * Extract audio files from cooked HTML and strip the native elements.
 *
 * Handles two Discourse patterns:
 * 1. `<audio controls><source src="URL" type="audio/..."></audio>`
 * 2. `<a class="attachment" href="URL">FILENAME</a>` where URL has an audio extension
 */
export function extractAndStripAudio(html: string): {
  html: string;
  audioFiles: Array<{ src: string; filename: string }>;
} {
  const audioFiles: Array<{ src: string; filename: string }> = [];

  // 1. Native <audio> elements
  let result = html.replace(
    /<audio\b[^>]*>[\s\S]*?<\/audio>/gi,
    (match) => {
      const srcMatch = match.match(/<source\b[^>]*src="([^"]+)"/i);
      if (!srcMatch) return match;
      const src = srcMatch[1];
      const namePart = src.split("/").pop()?.split("?")[0] ?? "lydklip";
      audioFiles.push({ src, filename: decodeURIComponent(namePart) });
      return "";
    }
  );

  // 2. Attachment links pointing to audio files
  result = result.replace(
    /<a\b[^>]*class="[^"]*\battachment\b[^"]*"[^>]*href="([^"]+)"[^>]*>([^<]*)<\/a>/gi,
    (match, href: string, text: string) => {
      if (!AUDIO_EXTENSIONS.test(href)) return match;
      audioFiles.push({ src: href, filename: text.trim() || decodeURIComponent(href.split("/").pop()?.split("?")[0] ?? "lydklip") });
      return "";
    }
  );

  // Also handle href-before-class attribute order
  result = result.replace(
    /<a\b[^>]*href="([^"]+)"[^>]*class="[^"]*\battachment\b[^"]*"[^>]*>([^<]*)<\/a>/gi,
    (match, href: string, text: string) => {
      if (!AUDIO_EXTENSIONS.test(href)) return match;
      audioFiles.push({ src: href, filename: text.trim() || decodeURIComponent(href.split("/").pop()?.split("?")[0] ?? "lydklip") });
      return "";
    }
  );

  // Clean up paragraphs left empty
  result = result.replace(/<p>\s*<\/p>/gi, "");

  return { html: result, audioFiles };
}

/**
 * Cooked HTML as readable plain text: strips tags and images, decodes
 * entities, collapses whitespace. Used wherever a message needs to read as a
 * one-line preview rather than be rendered — a lightbox caption, a quoted
 * reply, a message-request preview.
 */
export function cookedToPlainText(html: string): string {
  /**
   * An emoji image keeps its `alt` — migrated or custom, `EMOJI_CLASS_SOURCE`
   * knows both — and every other image is dropped (OSO-501).
   * The same split the server makes in `htmlExcerpt`: an attachment is a file
   * the message carries and the caller has a chip for it, an emoji is text the
   * author typed. Without this, quoting an emoji-only message shows
   * "📷 Billede" — an image chip for something that is not an image. `alt` is
   * the shortcode, set by the renderer's emoji rule.
   */
  return (
    textOf(
      html.replace(/<img\b[^>]*>/gi, (tag) =>
        EMOJI_IMG_CLASS.test(tag)
          ? ` ${/\balt="([^"]*)"/.exec(tag)?.[1] ?? ""} `
          : " ",
      ),
    ) ?? ""
  );
}

/** `decodeURIComponent`, but a malformed `%` sequence returns the input as-is
 *  instead of throwing — an upload filename is untrusted input. */
function safeDecodeURIComponent(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/** The filename's extension (lowercase, no dot), or `""` if it has none. */
function extOf(filename: string): string {
  return filename.match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toLowerCase() ?? "";
}

/** A non-audio file attached via `<a class="attachment">`. */
export interface FileRef {
  href: string;
  filename: string;
  /** Lowercase, no dot — `""` when the filename has none. */
  ext: string;
  /** The size Discourse appends after the link, e.g. `"149 KB"`, if present. */
  size?: string;
}

/** Only a Discourse-shaped size annotation — never swallows unrelated
 *  parenthetical text that happens to follow a link. */
const SIZE_RE = /\s*\(([\d.,]+\s?(?:Bytes|KB|MB|GB))\)/i;

/**
 * Extract non-audio files from cooked HTML and strip the anchors.
 *
 * Mirrors {@link extractAndStripAudio}'s `<a class="attachment">` handling —
 * run this *after* the audio pass so an audio attachment link is already gone
 * and cannot be double-counted as a file (the extension check below is a
 * second guard in case this is ever called on its own).
 */
export function extractAndStripFiles(html: string): {
  html: string;
  files: FileRef[];
} {
  const files: FileRef[] = [];

  const handle = (match: string, href: string, text: string, size?: string) => {
    if (AUDIO_EXTENSIONS.test(href)) return match;
    const decodedHref = decodeEntities(href);
    const hrefName = safeDecodeURIComponent(
      decodedHref.split("/").pop()?.split("?")[0] ?? "fil"
    );
    const filename = decodeEntities(text).trim() || hrefName;
    files.push({
      href: decodedHref,
      filename,
      ext: extOf(filename) || extOf(hrefName),
      size: size?.trim(),
    });
    return "";
  };

  // 1. class before href
  let result = html.replace(
    new RegExp(
      `<a\\b[^>]*class="[^"]*\\battachment\\b[^"]*"[^>]*href="([^"]+)"[^>]*>([^<]*)<\\/a>(?:${SIZE_RE.source})?`,
      "gi"
    ),
    (match, href: string, text: string, size?: string) => handle(match, href, text, size)
  );

  // 2. href before class
  result = result.replace(
    new RegExp(
      `<a\\b[^>]*href="([^"]+)"[^>]*class="[^"]*\\battachment\\b[^"]*"[^>]*>([^<]*)<\\/a>(?:${SIZE_RE.source})?`,
      "gi"
    ),
    (match, href: string, text: string, size?: string) => handle(match, href, text, size)
  );

  // Clean up paragraphs left empty
  result = result.replace(/<p>\s*<\/p>/gi, "");

  return { html: result, files };
}

function youtubeEmbed(videoId: string): string {
  if (!/^[a-zA-Z0-9_-]{11}$/.test(videoId)) return "";
  // No `autoplay` in the allow-list: an embed must never be able to start
  // itself in a feed (OSO-437).
  return `<div class="youtube-embed"><iframe src="https://www.youtube-nocookie.com/embed/${videoId}" allow="fullscreen" allowfullscreen loading="lazy"></iframe></div>`;
}
